import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Cookies from "js-cookie";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import { getConsultationsByDoctor } from "../../consultation/redux/thunks/consultationThunks";
import { selectDoctorConsultations } from "../../consultation/redux/selectors/consultationSelectors";

/* ── Rendezvous ── */
import {
  fetchRendezvousByMedecin,
  confirmRendezvous,
  cancelRendezvous,
} from "../../appointments/redux/thunks/rendezvousThunks";
import { addLocalRdv } from "../../appointments/redux/slices/rendezvousSlice";
import {
  selectRdvByMedecin,
  selectRdvByMedecinLoading,
} from "../../appointments/redux/selectors/rendezvousSelectors";

/* ── Events (availabilityDoctor) ── */
import { fetchEventsByDoctor } from "../../availabilityDoctor/redux/thunks/eventsThunks";
import {
  fetchAllDoctorSlots,
  fetchBlockedSlots,
} from "../../availabilityDoctor/redux/thunks/disponibilityThunks";
import { selectAllDisponibilitySlots } from "../../availabilityDoctor/redux/selectors/disponibilitySelectors";
import {
  selectEventsList,
  selectEventsLoading,
} from "../../availabilityDoctor/redux/selectors/eventsSelectors";

/* ── Local Patients ── */
import { fetchLocalPatients } from "../../WaitingRoom/redux/thunks/localPatient.thunks";
import {
  selectAllLocalPatients,
} from "../../WaitingRoom/redux/selectors/localPatient.selectors";
import { fetchUserById, fetchUsers } from "../../Profile/redux/thunks/userThunks";
import { axiosRDV } from "../../api/axios";
import { DEFAULT_ACCESS_SCOPES } from "../../authorization/useMedicalCapabilities";

/* ── Rooms (WaitingRoom) ── */
import { fetchRoomsByStatus } from "../../WaitingRoom/redux/thunks/room.thunks";
import { selectAllRooms } from "../../WaitingRoom/redux/selectors/room.selectors";

/* ── Cabinet Dashboard slice ── */
import {
  selectAnchorDate,
  selectViewMode,
  selectDashboardStatsLoading,
} from "../redux/selectors/cabinetDashboardSelectors";
import {
  setAnchorDate,
  setViewMode,
} from "../redux/slices/cabinetDashboardSlice";
import { calculateTodayOverview } from "../utils/todayOverview";
import { getWorkingPeriods } from "../utils/availabilitySchedule";

/* ── helpers ─────────────────────────────────────── */
const pad = (n) => String(n).padStart(2, "0");

const extractSlotsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.slots)) return payload.slots;
  if (Array.isArray(payload?.data?.slots)) return payload.data.slots;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export function toDateStr(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromDateStr(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function getWeekDates(anchorStr) {
  const anchor = fromDateStr(anchorStr);
  const day = anchor.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);
    return dt;
  });
}

/* ── Patient indexing & enrichment ── */
const extractIdCandidates = (obj) => {
  if (obj == null) return [];
  if (typeof obj === "string" || typeof obj === "number") return [String(obj)];
  const keys = [
    "id",
    "_id",
    "patientId",
    "patientIdentifier",
    "identifier",
    "externalId",
    "uuid",
    "userId",
    "patientUserId",
    "patientExternalId",
    "patientUUID",
    "cin",
    "n_assurance",
    "profileId",
    "subprofileId",
  ];
  const out = new Set();
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null) out.add(String(v));
  }
  // also inspect nested common containers
  if (obj.user && typeof obj.user === "object") {
    for (const v of extractIdCandidates(obj.user)) out.add(v);
  }
  if (obj.patient && typeof obj.patient === "object") {
    for (const v of extractIdCandidates(obj.patient)) out.add(v);
  }
  if (obj.account && typeof obj.account === "object") {
    for (const v of extractIdCandidates(obj.account)) out.add(v);
  }
  return Array.from(out).filter(Boolean);
};

const buildPatientIndex = (patients) => {
  const byId = new Map();
  const items = Array.isArray(patients) ? patients : Object.values(patients || {});
  for (const p of items) {
    if (!p) continue;
    const candidates = extractIdCandidates(p);
    // also include top-level primitive patient fields
    const extras = [p?.id, p?._id, p?.identifier, p?.patientIdentifier, p?.uuid];
    for (const e of extras) if (e != null) candidates.push(String(e));
    for (const key of candidates) {
      if (key != null) byId.set(String(key), p);
    }
  }
  return byId;
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
};

const pickIdentifier = (u) =>
  u?.identifier || u?.cin || u?.n_assurance || u?.externalId || u?.uuid || "";

const joinName = (...parts) =>
  parts
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const looksLikeId = (value) => {
  const text = String(value || "").trim();
  if (!text) return true;
  if (/^patient\b/i.test(text) && /[0-9a-f]{6,}/i.test(text)) return true;
  if (/^[0-9]+$/.test(text)) return true;
  if (/^[0-9a-f]{8,}$/i.test(text)) return true;
  if (/^[0-9a-f]{8,}(-[0-9a-f]{4,}){3,4}$/i.test(text)) return true;
  return false;
};

const normalizeDisplayName = (value) => {
  const text = typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
  return text && !looksLikeId(text) ? text : "";
};

const isLocalNumericId = (value) => /^\d{8,}$/.test(String(value || "").trim());

const isUuidLikeId = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );

const pickPatientName = ({ localPatient, medilinkUser, rendezvous }) => {
  const candidates = [
    localPatient?.fullName,
    localPatient?.name,
    joinName(localPatient?.firstName, localPatient?.lastName),
    localPatient?.displayName,
    medilinkUser?.fullName,
    medilinkUser?.name,
    medilinkUser?.displayName,
    medilinkUser?.fullNameAr,
    medilinkUser?.nameAr,
    joinName(medilinkUser?.firstName, medilinkUser?.lastName),
    joinName(medilinkUser?.firstNameAr, medilinkUser?.lastNameAr),
    rendezvous?.patientName,
    rendezvous?.userName,
    rendezvous?.patient?.fullName,
    rendezvous?.patient?.name,
    rendezvous?.patient?.displayName,
    rendezvous?.patient?.fullNameAr,
    joinName(rendezvous?.patient?.firstName, rendezvous?.patient?.lastName),
    joinName(rendezvous?.patient?.firstNameAr, rendezvous?.patient?.lastNameAr),
  ];

  for (const candidate of candidates) {
    const name = normalizeDisplayName(candidate);
    if (name) return name;
  }

  return "Patient";
};

/* ── hook ────────────────────────────────────────── */
export default function useCabinetDashboard({ medecinIdOverride = null } = {}) {
  const dispatch = useDispatch();
  const { t } = useTranslation("cabinetDashboard");
  const fetchedPatientIdsRef = useRef(new Set());

  const locale = t("dashboard.locale", { defaultValue: "en-US" });

  /* ── Cabinet dashboard UI state ── */
  const anchorDate  = useSelector(selectAnchorDate);
  const viewMode    = useSelector(selectViewMode);
  const statsLoading = useSelector(selectDashboardStatsLoading);

  /* ── Authenticated doctor id ── */
  const authUser = useSelector((s) => s.auth?.user);
  const doctorUser = useSelector((s) => s.doctor?.user);
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const medecinId =
    medecinIdOverride ||
    doctorUser?.id || doctorUser?._id || doctorUser?.doctorId || doctorUser?.medecinId ||
    authUser?.id || authUser?._id || authUser?.doctorId || authUser?.medecinId || authUser?.userId ||
    effectiveUserId ||
    Cookies.get("doctor_id") || Cookies.get("userId") || null;

  /* ── Local patients ── */
  const localPatients = useSelector(selectAllLocalPatients);
  const localPatientIndex = useMemo(() => buildPatientIndex(localPatients), [localPatients]);
  const medilinkUsersById = useSelector((s) => s.user?.usersById || {});
  const medilinkUsers = useSelector((s) => s.user?.users || {});
  const hasMedilinkUsers =
    Object.keys(medilinkUsersById).length > 0 || Object.keys(medilinkUsers).length > 0;
  const medilinkUserIndex = useMemo(
    () => buildPatientIndex({ ...medilinkUsersById, ...medilinkUsers }),
    [medilinkUsersById, medilinkUsers]
  );

  const location = useLocation();

  /* ── Raw Redux data ── */
  const rdvItems    = useSelector(selectRdvByMedecin);      // rendezvous.byMedecin.items
  const rdvLoading  = useSelector(selectRdvByMedecinLoading);
  const evItems     = useSelector(selectEventsList);         // events.list
  const evLoading   = useSelector(selectEventsLoading);
  const availabilitySlots = useSelector(selectAllDisponibilitySlots);
  const workingPeriods = useMemo(() => getWorkingPeriods(availabilitySlots), [availabilitySlots]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [accessByPatient, setAccessByPatient] = useState({});

  /* ── Rooms data ── */
  const allRooms = useSelector(selectAllRooms);
  const doctorConsultations = useSelector(selectDoctorConsultations);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => new Date());
  const [roomsReady, setRoomsReady] = useState(false);

  const calendarLoading = rdvLoading || evLoading;

  /* ── Fetch RDV + Events + patient caches on mount / when doctor id changes ── */
  useEffect(() => {
    if (!medecinId) return;
    dispatch(fetchRendezvousByMedecin({ userId: medecinId }));
    dispatch(fetchEventsByDoctor(medecinId));
    dispatch(fetchAllDoctorSlots({ medecinId }));
    dispatch(fetchLocalPatients({ activeOnly: true }));
    if (!hasMedilinkUsers) {
      dispatch(fetchUsers());
    }
  }, [dispatch, medecinId, location.pathname, hasMedilinkUsers]);

  /* ── Fetch rooms (waiting, completed, etc.) ── */
  // Blocked availability must remain visible in the cabinet calendar.
  useEffect(() => {
    if (!medecinId) {
      setBlockedSlots([]);
      return;
    }

    let isCurrent = true;
    dispatch(fetchBlockedSlots({ medecinId, date: anchorDate }))
      .unwrap()
      .then((payload) => {
        if (isCurrent) setBlockedSlots(extractSlotsPayload(payload));
      })
      .catch(() => {
        if (isCurrent) setBlockedSlots([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [dispatch, medecinId, anchorDate, location.pathname]);

  useEffect(() => {
    if (!medecinId) {
      setAccessByPatient({});
      return undefined;
    }

    let mounted = true;
    axiosRDV
      .get(`/access-requests/doctor/${encodeURIComponent(String(medecinId))}`)
      .then(({ data }) => {
        if (!mounted) return;
        const items = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const next = {};
        items.forEach((item) => {
          const patientId = String(item?.patientId ?? "").trim();
          if (!patientId) return;
          const candidate = {
            status: String(item?.status || "pending").toLowerCase(),
            allowProfile: Boolean(item?.allowProfile),
          };
          if (!next[patientId] || (candidate.status === "accepted" && next[patientId].status !== "accepted")) {
            next[patientId] = candidate;
          }
        });
        setAccessByPatient(next);
      })
      .catch(() => {
        if (mounted) setAccessByPatient({});
      });

    return () => {
      mounted = false;
    };
  }, [medecinId]);
  useEffect(() => {
    if (!medecinId) {
      setRoomsReady(false);
      return undefined;
    }

    let active = true;
    setRoomsReady(false);
    Promise.all([
      dispatch(fetchRoomsByStatus({ status: "waiting" })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "completed" })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "confirmed" })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "in_consultation" })).unwrap(),
    ])
      .then(() => {
        if (active) setRoomsReady(true);
      })
      .catch((error) => {
        console.error("Unable to load cabinet waiting room data", error);
        if (active) {
          message.error(
            t("dashboard.refreshFailed", {
              defaultValue: "Impossible de mettre à jour le tableau de bord.",
            })
          );
        }
      });

    return () => {
      active = false;
    };
  }, [dispatch, medecinId, t]);

  useEffect(() => {
    if (!medecinId) return undefined;

    const refreshDashboard = async () => {
      try {
        await Promise.all([
          dispatch(fetchRendezvousByMedecin({ userId: medecinId })).unwrap(),
          dispatch(fetchRoomsByStatus({ status: "waiting" })).unwrap(),
          dispatch(fetchRoomsByStatus({ status: "completed" })).unwrap(),
          dispatch(fetchRoomsByStatus({ status: "confirmed" })).unwrap(),
          dispatch(fetchRoomsByStatus({ status: "in_consultation" })).unwrap(),
          dispatch(fetchEventsByDoctor(medecinId)).unwrap(),
        ]);
        setRoomsReady(true);
        setLastUpdatedAt(new Date());
      } catch (error) {
        console.error("Unable to refresh cabinet dashboard data", error);
        message.error(
          t("dashboard.refreshFailed", {
            defaultValue: "Impossible de mettre à jour le tableau de bord.",
          })
        );
      }
    };

    const interval = window.setInterval(refreshDashboard, 45_000);
    return () => window.clearInterval(interval);
  }, [dispatch, medecinId, t]);

  /* ── Consultations used by Today's Revenue ── */
  useEffect(() => {
    if (!medecinId) return;
    dispatch(getConsultationsByDoctor(medecinId));
  }, [dispatch, medecinId]);

  useEffect(() => {
    if (!rdvItems?.length) return;

    const unresolved = new Set();

    for (const r of rdvItems) {
      const candidates = [r?.patientId, r?.patient, r?.userId, r?.patientUserId];
      for (const cand of candidates) {
        for (const id of extractIdCandidates(cand)) {
          if (localPatientIndex.has(id) || medilinkUserIndex.has(id)) continue;
          unresolved.add(id);
        }
      }
    }

    unresolved.forEach((id) => {
      if (fetchedPatientIdsRef.current.has(id)) return;
      fetchedPatientIdsRef.current.add(id);
      // log once per id so we can inspect runtime mismatches
      try {
        // eslint-disable-next-line no-console
        console.debug("useCabinetDashboard: fetching unresolved patient id", id);
      } catch (e) {}
      const appointment = rdvItems.find((item) =>
        extractIdCandidates(item?.subprofileId || item?.effectiveProfileId).includes(id)
      );
      dispatch(
        appointment?.subprofileId
          ? fetchUserById({
              userId: appointment.subprofileId,
              principalUserId: appointment.patientId,
              subprofileId: appointment.subprofileId,
            })
          : fetchUserById(id)
      );
    });
  }, [dispatch, rdvItems, localPatientIndex, medilinkUserIndex]);

  /* ── Normalize RDV → calendar appointment shape with patient enrichment ── */
  const rdvAppointments = useMemo(() => {
    if (!rdvItems?.length) return [];
    return rdvItems.map((r) => {
      const start = r.startTime ? new Date(r.startTime) : null;
      if (!start) return null;

      const date  = toDateStr(start);
      const hour  = start.getHours();
      const minute = start.getMinutes();
      const time  = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

      /* ── Resolve patient info from local or medilink ── */
      let localPatient = null;
      let medilinkUser = null;
      const subprofileId = String(
        r.subprofileId || r.subProfileId ||
        (r.effectiveProfileId && String(r.effectiveProfileId) !== String(r.patientId)
          ? r.effectiveProfileId
          : "")
      ).trim();
      const subprofile = r.subprofile || r.subProfile || null;
      const patientIdKeys = Array.from(
        new Set([
          ...extractIdCandidates(r.patientId),
          ...extractIdCandidates(r.patient),
          ...extractIdCandidates(r),
          ...(r.userId ? [String(r.userId)] : []),
        ])
      ).filter(Boolean);

      for (const k of patientIdKeys) {
        if (localPatientIndex.has(k)) {
          localPatient = localPatientIndex.get(k);
          break;
        }
      }

      if (!localPatient) {
        for (const k of patientIdKeys) {
          if (medilinkUserIndex.has(k)) {
            medilinkUser = medilinkUserIndex.get(k);
            break;
          }
        }
      }

      if (!localPatient && !medilinkUser) {
        try {
          // eslint-disable-next-line no-console
          console.debug("useCabinetDashboard: unresolved rdv", r.id ?? r._id, patientIdKeys);
        } catch (e) {}
      }

      const hasLocalHint =
        String(r.patientSource || r.source || "").toLowerCase() === "local" ||
        patientIdKeys.some((k) => isLocalNumericId(k));
      const hasMedilinkHint =
        String(r.patientSource || r.source || "").toLowerCase() === "medilink" ||
        patientIdKeys.some((k) => isUuidLikeId(k));

      const source = subprofileId
        ? "subprofile"
        : localPatient || (hasLocalHint && !hasMedilinkHint)
        ? "local"
        : "medilink";
      const patientName = pickPatientName({
        localPatient,
        medilinkUser: subprofile || medilinkUser,
        rendezvous: r,
      });

      const age = calculateAge(
        localPatient?.dateOfBirth ||
        subprofile?.dateOfBirth ||
        subprofile?.birthdate ||
        medilinkUser?.dateOfBirth ||
        medilinkUser?.birthdate ||
        medilinkUser?.birthDate ||
        r.patient?.dateOfBirth ||
        r.patient?.dob
      );
      const identifier =
        pickIdentifier(subprofile) ||
        pickIdentifier(localPatient) ||
        pickIdentifier(medilinkUser) ||
        pickIdentifier(r.patient) ||
        (subprofileId ? "" : String(r.patientId || "").slice(0, 8));
      const patientId = patientIdKeys[0] || null;

      const rawStatus = String(r.status || "").toLowerCase();
      const status = ["in_consultation", "in_progress", "in-progress"].includes(rawStatus)
        ? "in-progress"
        : ["done", "canceled"].includes(rawStatus)
          ? rawStatus === "done" ? "completed" : "cancelled"
          : ["confirmed", "pending", "cancelled", "waiting", "completed", "rescheduled"].includes(rawStatus)
            ? rawStatus
            : rawStatus || "confirmed";

      const notes = r.reason || r.notes || r.comment || "";

      return {
        id: `rdv-${r.id ?? r._id}`,
        rdvId: r.id ?? r._id,
        patientId,
        subprofileId: subprofileId || null,
        effectiveProfileId: r.effectiveProfileId || subprofileId || patientId,
        accessAllowed: Boolean(patientId && accessByPatient[patientId]?.allowProfile),
        source: "rdv",
        date,
        hour,
        minute,
        time,
        patient: patientName,
        patientIdentifier: identifier,
        patientAge: age,
        patientSource: subprofileId
          ? "subprofile"
          : localPatient
          ? "local"
          : medilinkUser
          ? "medilink"
          : source,
        notes,
        status,
      };
    }).filter(Boolean);
  }, [rdvItems, localPatientIndex, medilinkUserIndex, accessByPatient]);

  /* ── Normalize Events → calendar appointment shape ── */
  const evAppointments = useMemo(() => {
    if (!evItems?.length) return [];
    return evItems.map((e) => {
      const start = e.startTime ? new Date(e.startTime) : null;
      if (!start) return null;

      const date = toDateStr(start);
      const hour = start.getHours();
      const minute = start.getMinutes();
      const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

      return { id: `ev-${e.id}`, rdvId: null, source: "event",
               date, hour, minute, time, patient: e.title || "Event",
               notes: e.location || e.place || e.description || "",
               status: "confirmed" };
    }).filter(Boolean);
  }, [evItems]);

  /* ── Merged appointments (RDV first, then events) ── */
  const appointments = useMemo(
    () => [...rdvAppointments, ...evAppointments],
    [rdvAppointments, evAppointments]
  );

  const today = useMemo(() => new Date(), []);

  const weekDates = useMemo(() => getWeekDates(anchorDate), [anchorDate]);

  const filteredAppointments = useMemo(() => {
    if (!appointments?.length) return [];

    const anchor = fromDateStr(anchorDate);
    const month = anchor.getMonth();
    const year  = anchor.getFullYear();
    const weekDateSet = new Set(weekDates.map((d) => toDateStr(d)));

    return appointments.filter((appt) => {
      if (!appt?.date) return false;
      if (viewMode === "Day") {
        return appt.date === anchorDate;
      }
      if (viewMode === "Week") {
        return weekDateSet.has(appt.date);
      }
      if (viewMode === "Month") {
        const apptDate = fromDateStr(appt.date);
        return apptDate.getFullYear() === year && apptDate.getMonth() === month;
      }
      return true;
    });
  }, [appointments, viewMode, anchorDate, weekDates]);

  const weekLabel = useMemo(() => {
    if (viewMode === "Day") {
      const anchor = fromDateStr(anchorDate);
      return anchor.toLocaleDateString(locale, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    const first = weekDates[0];
    return `${t("dashboard.weekOf")} ${first.toLocaleDateString(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
    })}`;
  }, [weekDates, viewMode, anchorDate, locale, t]);

  const sessionLabel = useMemo(() => {
    const h = today.getHours();
    if (h < 12) return t("dashboard.session.morning");
    if (h < 17) return t("dashboard.session.afternoon");
    return t("dashboard.session.evening");
  }, [today, t]);

  const todayLabel = today.toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const goBack = useCallback(() => {
    const d = fromDateStr(anchorDate);
    if (viewMode === "Day") d.setDate(d.getDate() - 1);
    else if (viewMode === "Week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    dispatch(setAnchorDate(toDateStr(d)));
  }, [anchorDate, viewMode, dispatch]);

  const goForward = useCallback(() => {
    const d = fromDateStr(anchorDate);
    if (viewMode === "Day") d.setDate(d.getDate() + 1);
    else if (viewMode === "Week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    dispatch(setAnchorDate(toDateStr(d)));
  }, [anchorDate, viewMode, dispatch]);

  const goToday = useCallback(() => {
    dispatch(setAnchorDate(toDateStr(new Date())));
  }, [dispatch]);

  const changeViewMode = useCallback(
    (mode) => dispatch(setViewMode(mode)),
    [dispatch]
  );

  /* ── Confirm a pending RDV ── */
  const confirmAppointment = useCallback(
    (appt) => {
      if (!appt?.rdvId || appt.source !== "rdv") return;
      dispatch(confirmRendezvous({ userId: medecinId, id: appt.rdvId }));
    },
    [dispatch, medecinId]
  );

  /* ── Cancel a RDV (doctor side) ── */
  const cancelAppointment = useCallback(
    (appt) => {
      if (!appt?.rdvId || appt.source !== "rdv") return;
      dispatch(cancelRendezvous({ userId: medecinId, rendezvousId: appt.rdvId, by: "doctor" }));
    },
    [dispatch, medecinId]
  );

  /* ── Add the appointment already created by the booking modal ── */
  const bookAppointment = useCallback(
    ({
      booked,
      patient,
      selectedSlot,
      reason,
    }) => {
      if (!booked?.appointmentId || !selectedSlot?.startTime) return;

      dispatch(addLocalRdv({
        id: booked.appointmentId,
        patientId: booked.patientId || patient?.id,
        patientName: patient?.name,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        reason: reason || "Consultation",
        status: "confirmed",
        source: "rdv",
      }));
    },
    [dispatch]
  );

  /* ── Calculate real-time stats from actual data sources ── */
  const calculatedStats = useMemo(() => {
    const liveStats = calculateTodayOverview({
      rendezvous: rdvItems,
      rooms: allRooms,
      consultations: doctorConsultations,
      doctorId: medecinId,
      now: today,
    });
    const appointmentsDeltaPrefix = liveStats.appointmentsDelta > 0 ? "+" : "";
    const revenueDeltaPrefix = liveStats.revenueDelta > 0 ? "+" : "";
    const formattedRevenue = `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 3,
    }).format(liveStats.revenue)} TND`;

    return {
      appointments: liveStats.appointments,
      waiting: liveStats.waiting,
      completed: liveStats.completed,
      revenue: formattedRevenue,
      revenueValue: liveStats.revenue,
      sparklines: liveStats.sparklines,
      appointmentsDelta: `${appointmentsDeltaPrefix}${liveStats.appointmentsDelta} ${t(
        "dashboard.overview.fromYesterday",
        { defaultValue: "from yesterday" }
      )}`,
      appointmentsDeltaValue: liveStats.appointmentsDelta,
      completionRate: liveStats.appointments > 0
        ? `${Math.round((liveStats.completed / liveStats.appointments) * 100)}%`
        : "0%",
      revenueDelta: `${revenueDeltaPrefix}${liveStats.revenueDelta}% ${t(
        "dashboard.overview.vsAverage",
        { defaultValue: "vs avg" }
      )}`,
      revenueDeltaValue: liveStats.revenueDelta,
    };
  }, [today, rdvItems, allRooms, doctorConsultations, medecinId, locale, t]);

  /* ── Dev debug surface: expose runtime snapshots and small overlay ── */
  useEffect(() => {
    try {
      // expose snapshot for manual inspection
      // eslint-disable-next-line no-undef
      if (typeof window !== "undefined") {
        // eslint-disable-next-line no-console
        window.__CABINET_DEBUG__ = {
          medecinId,
          effectiveUserId,
          doctorUserId: doctorUser?.id || doctorUser?._id || doctorUser?.doctorId || doctorUser?.medecinId || null,
          authUserId: authUser?.id || authUser?._id || authUser?.doctorId || authUser?.medecinId || authUser?.userId || null,
          rdvCount: Array.isArray(rdvItems) ? rdvItems.length : 0,
          localPatientsCount: Array.isArray(localPatients) ? localPatients.length : Object.keys(localPatients || {}).length,
          unresolvedSample: null,
        };

        // attach small overlay for quick visibility (only in non-production)
        if (process && process.env && process.env.NODE_ENV !== "production") {
          let el = document.getElementById("cabinet-debug-overlay");
          if (!el) {
            el = document.createElement("div");
            el.id = "cabinet-debug-overlay";
            el.style.position = "fixed";
            el.style.right = "8px";
            el.style.bottom = "8px";
            el.style.zIndex = 99999;
            el.style.background = "rgba(0,0,0,0.6)";
            el.style.color = "#fff";
            el.style.fontSize = "12px";
            el.style.padding = "6px 8px";
            el.style.borderRadius = "4px";
            el.style.maxWidth = "320px";
            el.style.fontFamily = "monospace";
            document.body.appendChild(el);
          }
          const rdvCount = Array.isArray(rdvItems) ? rdvItems.length : 0;
          const lpCount = Array.isArray(localPatients) ? localPatients.length : Object.keys(localPatients || {}).length;
          el.textContent = `doctor:${medecinId || "none"} rdv:${rdvCount} patients:${lpCount}`;
        }
      }
    } catch (e) {}
  }, [medecinId, rdvItems, localPatients]);

  return {
    today,
    anchorDate,
    viewMode,
    weekDates,
    weekLabel,
    sessionLabel,
    todayLabel,
    stats: calculatedStats,
    statsLoading,
    appointments: filteredAppointments,
    allAppointments: appointments,
    rooms: allRooms,
    waitingRooms: allRooms.filter((room) => String(room?.status || "").toLowerCase() === "waiting"),
    roomsReady,
    consultations: doctorConsultations,
    lastUpdatedAt,
    accessByPatient,
    blockedSlots,
    workingPeriods,
    calendarLoading: !!calendarLoading,
    medecinId,
    confirmAppointment,
    cancelAppointment,
    bookAppointment,
    goBack,
    goForward,
    goToday,
    changeViewMode,
  };
}
